import api from "../api/axios";

/**
 * Helper untuk membuat log aktivitas user
 * @param {Object} logData - Data log user
 * @param {string} logData.user_id - ID pengguna
 * @param {string} logData.session_id - ID sesi
 * @param {string} logData.event_type - Tipe event (login, logout, view, click, etc.)
 * @param {string} logData.event_name - Nama event spesifik
 * @param {string} logData.screen_name - Nama layar/halaman
 * @param {string} logData.activity - Deskripsi aktivitas
 * @param {string} logData.user_ip - IP address user
 * @returns {Promise} Response dari server
 */
export const createUserLog = async (logData) => {
  try {
    const payload = {
      user_id: String(logData.user_id || "not_logged_in"),
      session_id: String(logData.session_id || generateSessionId()),
      event_type: String(logData.event_type || "unknown"),
      event_name: String(logData.event_name || "unknown"),
      screen_name: String(logData.screen_name || "unknown"),
      activity: String(logData.activity || "No activity description"),
      user_ip: String(logData.user_ip || (await getUserIP())),
    };

    // Validasi payload sebelum dikirim
    // console.log("Sending user log payload:", payload);

    // Pastikan tidak ada field yang undefined atau null
    Object.keys(payload).forEach((key) => {
      if (payload[key] === "undefined" || payload[key] === "null") {
        payload[key] = "unknown";
      }
    });

    const response = await api.post("/log/user-log", payload);

    // console.log("User log created successfully:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to create user log:", error);
    console.error("Error response:", error.response?.data);
    // Jangan throw error agar tidak mengganggu UX
    return null;
  }
};

/**
 * Helper untuk membuat log aktivitas admin
 * @param {Object} logData - Data log admin
 * @param {string} logData.admin_user_id - ID admin
 * @param {string} logData.admin_username - Username admin
 * @param {string} logData.event_type - Tipe event (create, update, delete, etc.)
 * @param {string} logData.event_name - Nama event spesifik
 * @param {string} logData.content_id - ID konten yang diaksi
 * @param {string} logData.content_type - Tipe konten (user, article, etc.)
 * @param {string} logData.content_title - Judul konten
 * @param {string} logData.action_status - Status aksi (success, failed)
 * @param {string} logData.error_message - Pesan error (jika ada)
 * @param {string} logData.details - Detail tambahan
 * @returns {Promise} Response dari server
 */
export const createAdminLog = async (logData) => {
  try {
    // Dapatkan IP terlebih dahulu
    const userIP = await getUserIP();

    const response = await api.post("/log/admin-log", {
      admin_user_id: logData.admin_user_id,
      admin_username: logData.admin_username,
      event_type: logData.event_type,
      event_name: logData.event_name,
      content_id: logData.content_id || "",
      content_type: logData.content_type || "",
      content_title: logData.content_title || "",
      action_status: logData.action_status || "success",
      error_message: logData.error_message || "",
      ip_address: userIP, // Pastikan IP didapat
      details: logData.details || "",
    });

    // console.log("Admin log created successfully:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to create admin log:", error);
    throw error;
  }
};

/**
 * Helper untuk generate session ID unik
 * @returns {string} Session ID
 */
const generateSessionId = () => {
  return (
    "session_" + Date.now() + "_" + Math.random().toString(36).substr(2, 9)
  );
};

/**
 * Helper untuk mendapatkan IP address user
 * @returns {Promise<string>} IP address
 */
const getUserIP = async () => {
  try {
    // Check if IP is cached and still valid
    const cachedIPData = localStorage.getItem("userIP");
    if (cachedIPData) {
      try {
        const { ip, timestamp } = JSON.parse(cachedIPData);
        const now = Date.now();
        const oneHour = 60 * 60 * 1000; // 1 hour in milliseconds
        
        // If cached IP is less than 1 hour old, use it
        if (now - timestamp < oneHour) {
          // console.log('Using cached IP:', ip);
          return ip;
        }
      } catch (parseError) {
        console.error("Error parsing cached IP data:", parseError);
        localStorage.removeItem("userIP");
      }
    }

    // If no valid cache, fetch new IP
    // console.log('Fetching fresh IP address...');
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

    const response = await fetch("https://api.ipify.org?format=json", {
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    const userIP = data.ip || "unknown";
    
    // Cache the IP with timestamp
    const cacheData = {
      ip: userIP,
      timestamp: Date.now()
    };
    localStorage.setItem("userIP", JSON.stringify(cacheData));
    
    // console.log('Fresh IP cached:', userIP);
    return userIP;
  } catch (error) {
    console.error("Failed to get user IP:", error);
    
    // Try to return cached IP even if expired as fallback
    const cachedIPData = localStorage.getItem("userIP");
    if (cachedIPData) {
      try {
        const { ip } = JSON.parse(cachedIPData);
        // console.log('Using expired cache as fallback:', ip);
        return ip;
      } catch (parseError) {
        // If cache is corrupted, clean it up
        localStorage.removeItem("userIP");
      }
    }
    
    return "unknown";
  }
};

/**
 * Helper untuk log aktivitas user dengan konteks yang sudah diisi otomatis
 * @param {string} eventType - Tipe event
 * @param {string} eventName - Nama event
 * @param {string} screenName - Nama layar
 * @param {string} activity - Deskripsi aktivitas
 */
export const logUserActivity = async (
  eventType,
  eventName,
  screenName,
  activity
) => {
  try {
    // Ambil user ID dari localStorage atau context
    const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
    const userId = userData.id || "not_logged_in";

    await createUserLog({
      user_id: userId.toString(),
      event_type: eventType,
      event_name: eventName,
      screen_name: screenName,
      activity: activity,
    });
  } catch (error) {
    console.error("Failed to log user activity:", error);
  }
};

/**
 * Helper untuk log aktivitas admin dengan konteks yang sudah diisi otomatis
 * @param {string} eventType - Tipe event
 * @param {string} eventName - Nama event
 * @param {string} contentType - Tipe konten
 * @param {string} contentId - ID konten
 * @param {string} actionStatus - Status aksi
 * @param {string} details - Detail tambahan
 */
export const logAdminActivity = async (
  eventType,
  eventName,
  contentType,
  contentId,
  actionStatus = "success",
  details = ""
) => {
  try {
    // Ambil admin data dari localStorage atau context
    const adminData = JSON.parse(localStorage.getItem("admin_data") || "{}");
    const adminId = adminData.id || "unknown";
    const adminUsername = adminData.username || "unknown";

    await createAdminLog({
      admin_user_id: adminId.toString(),
      admin_username: adminUsername,
      event_type: eventType,
      event_name: eventName,
      content_type: contentType,
      content_id: contentId.toString(),
      action_status: actionStatus,
      details: details,
    });
  } catch (error) {
    console.error("Failed to log admin activity:", error);
  }
};
